import type { ReactNode } from "react";

import { cx } from "class-variance-authority";
import type { TooltipProps } from "recharts";

import * as tooltipTheme from "../Tooltip/Tooltip.css";

import * as theme from "./StackedBarTrendChart.css";
import { StackedBarTrendChartBar, StackedBarTrendChartSeries } from "./types";

interface StackedBarTrendChartTooltipProps extends Pick<
    TooltipProps<number, string>,
    "active" | "payload" | "label"
> {
    series: StackedBarTrendChartSeries[];
    renderContent?: (bar: StackedBarTrendChartBar) => ReactNode;
}

export function StackedBarTrendChartTooltip({
    series,
    renderContent,
    active,
    label,
    payload,
}: StackedBarTrendChartTooltipProps) {
    const entry = payload?.[0];
    const hovered = series.find(({ key }) => key === entry?.dataKey);
    const value = entry?.value ?? 0;
    if (active !== true || hovered === undefined || value === 0) {
        return null;
    }

    // Item tooltips carry the row, but Recharts does not supply an axis label.
    const row: unknown = entry?.payload;
    const bucketLabel =
        typeof row === "object" &&
        row !== null &&
        "stackedBarTrendChartBucketLabel" in row
            ? row.stackedBarTrendChartBucketLabel
            : label;
    const bar: StackedBarTrendChartBar = {
        bucketLabel: String(bucketLabel ?? ""),
        series: hovered,
        value,
    };

    return (
        <div className={cx(tooltipTheme.content, theme.tooltip)}>
            {renderContent !== undefined ? (
                renderContent(bar)
            ) : (
                <>
                    <span className={theme.tooltipLabel}>
                        {bar.bucketLabel}
                    </span>
                    <span className={theme.tooltipRow}>
                        <span>{bar.series.label}</span>
                        <span>{bar.value}</span>
                    </span>
                </>
            )}
        </div>
    );
}
